/**
 * 广告命名模板：字段编辑弹窗。
 *
 * PR-AT-20260626-004 第 6 条：点击摘要卡上的 "编辑" / "+ 新增字段" 按钮时，
 * 在弹窗页面中展示对应字段的配置。
 *
 * - 弹窗内只承载单个字段的编辑表单（不包含上移/下移/删除/序号 badge）
 * - 弹窗内维护本地草稿；"保存" 提交到父组件，"取消" 丢弃草稿
 * - ESC 键与点击遮罩等同于"取消"
 */

import { useEffect, useState } from "react";
import type { TemplateField } from "../../types/adNameTemplate.types";
import { FieldEditorForm } from "./FieldEditor";

interface Props {
  open: boolean;
  /** 编辑现有字段时传入；新增时为 null */
  field: TemplateField | null;
  /** "edit" / "create"，仅用于弹窗标题文案 */
  mode: "edit" | "create";
  onSubmit: (next: TemplateField) => void;
  onCancel: () => void;
  /** 是否允许配置组合 2（缺省 true；华为每个字段只有 1 组枚举，传 false） */
  allowSecondCombo?: boolean;
}

export function FieldEditorModal({ open, field, mode, onSubmit, onCancel, allowSecondCombo = true }: Props) {
  // 每次打开弹窗时，复制一份草稿；关闭即丢弃
  const [draft, setDraft] = useState<TemplateField | null>(field);

  useEffect(() => {
    if (open) setDraft(field);
  }, [open, field]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open || !draft) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label={mode === "create" ? "新增字段" : "编辑字段"}
    >
      <div
        className="w-full max-w-[680px] rounded-2xl border border-line-default bg-bg-surface leo-card overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line-subtle px-6 py-4">
          <div>
            <div className="font-display text-[15px] font-semibold text-text-emphasis">
              {mode === "create" ? "新增字段" : "编辑字段"}
            </div>
            <div className="mt-0.5 text-[11.5px] text-text-muted">
              {draft.combos[0]?.combo_type === "SINGLE_ENUM"
                ? allowSecondCombo
                  ? "字段名 + 组合 1（枚举） + 组合 2（枚举，可选）"
                  : "字段名 + 组合 1（枚举）"
                : "字段名 + 组合 1（输入框）"}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="leo-btn-ghost px-2 py-1.5 text-[12px]"
            aria-label="关闭"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[68vh] overflow-y-auto px-6 py-5">
          <FieldEditorForm field={draft} onChange={setDraft} allowSecondCombo={allowSecondCombo} />
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-line-subtle bg-da-inset px-6 py-3.5">
          <button type="button" onClick={onCancel} className="leo-btn-ghost">
            取消
          </button>
          <button
            type="button"
            onClick={() => onSubmit(draft)}
            className="leo-cta-primary w-auto px-7"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
